/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LA LANDING EN INGLÉS Y EN PORTUGUÉS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 15/09/2026 · REESCRITA CON LA ESPAÑOLA (el porqué, entero, en `copy.ts`), y
 * otra vez ESCRITA EN CADA IDIOMA, no traducida. La versión anterior está en
 * git (commit `ae73324`).
 *
 * El aviso del dueño del 13/09 vale aquí más que en ningún sitio: «estás
 * traduciendo al pie de la letra, en lugar de adaptarlo a la realidad del
 * idioma». Así que el hilo es el mismo —doce bloques, la misma historia, y el
 * mismo número de puntos en cada uno, que `verificar:copy` lo exige— y las
 * frases no. El titular busca el mismo juego en cada idioma: en portugués
 * existe igual («ter voz»); en inglés, «to have a voice» es tener voz en el
 * sentido de contar, de que te escuchen.
 *
 * Lo que SÍ es idéntico es lo que no se puede decir: el filtro editorial corre
 * sobre los tres, con la misma lista de frases vetadas y de palabras que
 * declaran finalidad sanitaria.
 *
 * Y las comillas son las de cada mercado: “así” en inglés de EE. UU. y en
 * portugués de Brasil (y desde el 15/09 también en castellano, por el dueño).
 * ─────────────────────────────────────────────────────────────────────────── */

import { EN_TU_PLAN, FICHAS, PREGUNTAS, SILENCIOSAS, SIN_CARGA } from "@vc/contenido/cifras";
import { FOTOS, PRECIO, type Bloque } from "./precio.ts";

/* ═════════════════════════════════════════════════════════════════════════
   INGLÉS (EE. UU.)
   ═════════════════════════════════════════════════════════════════════════ */

export const BLOQUES_EN: readonly Bloque[] = [
  {
    id: "gancho",
    /* 16/09: decía «For people whose voice is their work». Cambia con el castellano («Para quien
       valora su voz», pedido del dueño; el porqué, en `copy.ts`). */
    eyebrow: "For anyone who values their voice",
    titulo: "“Having a voice” says it all. Take care of yours.",
    cuerpo:
      "It's how people hear you, choose you and remember you. Voice Comfort is the best, most " +
      "complete way to take care of it: a plan built around you, a few minutes a day on your phone, " +
      "and an assistant that knows voice inside out, whenever you need it.",
    foto: { src: FOTOS.manana, alt: "A woman smiles at her phone in her kitchen, coffee mug in hand." },
  },
  {
    id: "tu-voz",
    eyebrow: "Every single day",
    titulo: "Your voice works longer hours than you do.",
    cuerpo:
      "You teach, close deals, run meetings and take one call after another. It all runs on your " +
      "voice, and hardly anyone gives it a minute. Until the day it shows up tired right when you " +
      "need it most.",
  },
  {
    id: "se-entrena",
    eyebrow: "The good news",
    titulo: "Your voice can be trained, too.",
    cuerpo:
      "Just like you train your body, you can train your voice. You don't need to sing or know " +
      "anything about voice. You just need to know what to do, how much, and when to stop. That's " +
      "what we give you.",
    puntos: [
      { titulo: "What to do", texto: "Clear, simple exercises, explained step by step." },
      { titulo: "How much", texto: "The right amount of time and reps for your job and your day." },
      { titulo: "When to stop", texto: "A timer tells you when you're done, because with your voice, more isn't better." },
    ],
  },
  {
    id: "para-quien",
    eyebrow: "Who it's for",
    titulo: "Built for people who talk for a living.",
    cuerpo: "Every job uses the voice differently. That's why every plan is different.",
    puntos: [
      { titulo: "Teachers and trainers", texto: "Hours of class, with the last period as clear as the first." },
      { titulo: "Customer service and sales", texto: "One call after another, headset on." },
      { titulo: "Managers, lawyers and consultants", texto: "Meetings and presentations where your voice needs to sound confident." },
      { titulo: "Hosts, podcasters and creators", texto: "Hours on the mic, with your voice going strong to the end." },
      { titulo: "Singers, actors and voice-over artists", texto: "You already warm up before you perform. Here you take care of your everyday voice too." },
      { titulo: "Tour guides, coaches and instructors", texto: "Talking loud, outdoors and on the move." },
      { titulo: "Healthcare and care workers", texto: "Long shifts and conversations that call for calm." },
      { titulo: "And anyone who talks all day", texto: "If your voice feels tired by the end of the day, this is for you too." },
      /* 16/09: el rectángulo de abajo, pedido del dueño; contado entero en `copy.ts`. */
      {
        titulo: "And for everyone who values their voice and the power to communicate",
        texto:
          "Your job doesn't have to depend on your voice. At home, with friends or at work, it's how " +
          "you connect with people. Voice Comfort is for anyone who wants to take care of theirs.",
        destacado: true,
      },
    ],
  },
  {
    id: "como-funciona",
    eyebrow: "How it works",
    titulo: "Your plan, ready in five minutes.",
    cuerpo:
      `Out of ${FICHAS} exercises, we pick yours: between ${EN_TU_PLAN.min} and ${EN_TU_PLAN.max}, ` +
      "chosen for your job, how much you talk, and your day.",
    puntos: [
      {
        titulo: "Tell us how you use your voice",
        texto: `Your job, how many hours you talk, and ${PREGUNTAS} quick yes-or-no safety questions.`,
      },
      {
        titulo: "Let us hear you",
        texto: "Read one sentence out loud and do a gentle siren. It takes thirty seconds, and the audio never leaves your device.",
      },
      { titulo: "Get your plan", texto: "Your exercises, how much of each, and when to stop." },
      {
        titulo: "Practice a few minutes a day",
        texto: "If your voice is tired one day, or you can't make noise, that day's session adapts.",
      },
    ],
  },
  {
    id: "momento",
    eyebrow: "Any time of day",
    titulo: "It fits into your day.",
    cuerpo:
      `A few minutes a day on your phone, nothing to install. And ${SILENCIOSAS} exercises are ` +
      "silent, so nobody has to hear you.",
    puntos: [
      { titulo: "On your commute", texto: "On the bus or the train, earbuds in.", foto: { src: FOTOS.autobus, alt: "The same woman smiles at her phone by a bus window." } },
      { titulo: "Before class", texto: "Five minutes in the classroom before everyone arrives.", foto: { src: FOTOS.aula, alt: "She smiles at her phone, standing in an empty classroom." } },
      { titulo: "On a break", texto: "Between two calls, without leaving your chair.", foto: { src: FOTOS.oficina, alt: "She laughs at her phone at her office desk, work headset around her neck." } },
      { titulo: "Before a meeting", texto: "In the lobby, while you wait to be called in.", foto: { src: FOTOS.reunion, alt: "She smiles at her phone on a bench in an office lobby, dressed for a meeting." } },
      { titulo: "When the day is done", texto: "On the couch, letting your voice wind down.", foto: { src: FOTOS.sofa, alt: "She smiles at her phone on the couch under a blanket in the evening." } },
    ],
  },
  {
    id: "asistente",
    eyebrow: "Always there",
    titulo: "You're never on your own with your voice.",
    cuerpo:
      "An assistant that knows voice inside out, and knows your plan. Ask it anytime how to do an " +
      "exercise, why it's in your plan, or what to do when your voice is tired.",
    puntos: [
      { titulo: "Anytime", texto: "Before a class, halfway through a shift, or on a Sunday night." },
      { titulo: "With your plan in front of it", texto: "It answers about your exercises, not with generic tips." },
      { titulo: "Answers you can rely on", texto: "It draws on the same exercise library as your plan." },
    ],
  },
  {
    id: "diferenciales",
    eyebrow: "Why Voice Comfort",
    titulo: "The best, most complete way to take care of your voice.",
    puntos: [
      { titulo: "Exercises with a solid foundation", texto: "Based on professional voice practice and published research." },
      { titulo: "Hear how it should sound", texto: "Exercises that make a sound come with an example recording in your language." },
      { titulo: "See your voice live", texto: "While you practice, you see your pitch and volume on screen." },
      {
        titulo: "Reading practice that listens",
        texto: "When you read out loud, the words that come through clearly get marked on the text. Your voice never leaves your device.",
      },
      { titulo: "Made for rough days too", texto: `${SIN_CARGA} exercises that put no strain on your voice, for when it's tired.` },
      { titulo: "Your progress at a glance", texto: "See how many sessions you've done and how your plan is going." },
    ],
  },
  {
    id: "seguridad",
    eyebrow: "Safety first",
    titulo: "We take care of your voice, and we know when to stop.",
    cuerpo:
      `Before we build your plan, we ask you ${PREGUNTAS} safety questions. If any answer suggests ` +
      "you should see a professional, we tell you clearly and point you to the right person.",
    puntos: [
      { titulo: "Questions before you start", texto: "Short and yes-or-no, so your plan starts from how your voice is today." },
      { titulo: "We tell you when to get it checked", texto: "If something needs a professional's eye, we say so and tell you how soon." },
      { titulo: "A plan that eases off", texto: "When your voice is tired, your plan gets gentler so you can keep practicing with peace of mind." },
    ],
  },
  {
    id: "precio",
    eyebrow: "Pricing",
    titulo: "A whole year of taking care of your voice.",
    cuerpo:
      `All of Voice Comfort for ${PRECIO.moneda} ${PRECIO.lanzamientoUsd} a year.`,
    puntos: [
      { titulo: "Your personal plan", texto: "Built around your job and your day, and it changes with you." },
      { titulo: `All ${FICHAS} exercises`, texto: "Explained step by step, with example audio in your language." },
      { titulo: "The voice assistant", texto: "Always there, with your plan in front of it." },
      { titulo: "Your progress, session by session", texto: "See what you've done and what's next." },
      { titulo: "Cancel in one click", texto: "Right from your account, no emails needed." },
      /* La garantía de los catorce días: contada entera en `copy.ts`, en el mismo sitio. */
      {
        titulo: "Fourteen-day guarantee",
        texto: "If it's not for you, message us on WhatsApp within the first fourteen days and we'll refund what you paid.",
        destacado: true,
      },
    ],
  },
  {
    id: "preguntas",
    eyebrow: "FAQ",
    titulo: "What people ask before they start.",
    puntos: [
      { titulo: "Do I need to know music or sing?", texto: "No. Voice Comfort works on your speaking voice: the one you use to teach, sell or run a meeting." },
      { titulo: "How much time a day?", texto: "You pick: three, seven, fifteen or twenty-five minutes. The timer tells you when to stop." },
      { titulo: "What if I can't make noise one day?", texto: "Mark it when you start, and that day's session uses silent exercises." },
      { titulo: "What if my voice is tired?", texto: "Mark it and your session gets gentler. If something needs a professional's eye, we tell you." },
      { titulo: "Do I need to install anything?", texto: "No. It opens in your phone or computer browser." },
      { titulo: "What languages is it in?", texto: "English, Spanish and Portuguese, with example audio in each language." },
      { titulo: "How does the guarantee work?", texto: "If within the first fourteen days you decide it's not for you, message us on WhatsApp and we'll refund what you paid." },
      // 16/09/2026: said "… And we remind you before it renews."; that reminder doesn't exist (owner: remove it).
      { titulo: "Can I cancel anytime?", texto: "Yes, in one click from your account." },
    ],
  },
  {
    id: "cierre",
    eyebrow: "Start today",
    titulo: "Your voice is with you all day. Give it a few minutes.",
    cuerpo: "Build your plan in five minutes and start taking care of it today.",
  },
];

export const PIE_EN =
  "Voice Comfort trains a skill. It is not a medical device, it does not diagnose, and it isn't a " +
  "substitute for seeing a professional. When something needs a closer look, we tell you and point " +
  "you to the right person.";

/* ═════════════════════════════════════════════════════════════════════════
   PORTUGUÉS (BRASIL)
   ═════════════════════════════════════════════════════════════════════════ */

export const BLOQUES_PT: readonly Bloque[] = [
  {
    id: "gancho",
    /* 16/09: dizia «Para quem vive da voz». Muda com o castelhano (pedido do dono; o porquê, em
       `copy.ts`). */
    eyebrow: "Para quem valoriza sua voz",
    titulo: "“Ter voz” diz tudo. Cuide da sua.",
    cuerpo:
      "É pela sua voz que te ouvem, que te escolhem e que lembram de você. O Voice Comfort é a melhor " +
      "e mais completa plataforma para cuidar dela: um plano feito para você, alguns minutos por dia " +
      "no celular e um assistente especialista em voz sempre por perto.",
    foto: { src: FOTOS.manana, alt: "Uma mulher sorri olhando o celular na cozinha, com uma xícara de café na mão." },
  },
  {
    id: "tu-voz",
    eyebrow: "Todo santo dia",
    titulo: "Sua voz trabalha mais horas do que você.",
    cuerpo:
      "Você dá aula, fecha vendas, conduz reuniões e atende uma ligação atrás da outra. Tudo passa " +
      "pela sua voz, e quase ninguém tira um minuto para ela. Até o dia em que ela chega cansada " +
      "justo quando você mais precisa.",
  },
  {
    id: "se-entrena",
    eyebrow: "A boa notícia",
    titulo: "A voz também se treina.",
    cuerpo:
      "Do mesmo jeito que você treina o corpo, dá para treinar a voz. Não precisa cantar nem " +
      "entender nada de voz: basta saber o que fazer, quanto e quando parar. É isso que a gente te dá.",
    puntos: [
      { titulo: "O que fazer", texto: "Exercícios concretos, explicados passo a passo." },
      { titulo: "Quanto", texto: "O tempo e as repetições certas para o seu trabalho e o seu dia." },
      { titulo: "Quando parar", texto: "Um cronômetro avisa a hora de terminar, porque na voz, mais não é melhor." },
    ],
  },
  {
    id: "para-quien",
    eyebrow: "Para quem é",
    titulo: "Feito para quem vive da voz.",
    cuerpo: "Cada profissão usa a voz de um jeito. Por isso cada plano é diferente.",
    puntos: [
      { titulo: "Professores e instrutores", texto: "Horas de aula, com a última tão clara quanto a primeira." },
      { titulo: "Atendimento e vendas", texto: "Uma ligação atrás da outra, com o headset no ouvido." },
      { titulo: "Gestores, advogados e consultores", texto: "Reuniões e apresentações em que a voz precisa passar segurança." },
      { titulo: "Locutores, podcasters e criadores", texto: "Horas de microfone, com a voz inteira até o fim." },
      { titulo: "Cantores, atores e dubladores", texto: "Você já aquece antes de se apresentar. Aqui cuida também da voz do dia a dia." },
      { titulo: "Guias, treinadores e professores de academia", texto: "Falar alto, ao ar livre e em movimento." },
      { titulo: "Profissionais da saúde e do cuidado", texto: "Plantões longos e conversas que pedem calma." },
      { titulo: "E todo mundo que fala o dia inteiro", texto: "Se no fim do dia a voz chega cansada, isto também é para você." },
      /* 16/09: el rectángulo de abajo, pedido del dueño; contado entero en `copy.ts`. */
      {
        titulo: "E para todos que valorizam a própria voz e o poder de se comunicar",
        texto:
          "Você não precisa trabalhar com a voz. Em casa, com os amigos ou no trabalho, é com ela que " +
          "você se comunica. O Voice Comfort é para qualquer pessoa que queira cuidar dela.",
        destacado: true,
      },
    ],
  },
  {
    id: "como-funciona",
    eyebrow: "Como funciona",
    titulo: "Seu plano, pronto em cinco minutos.",
    cuerpo:
      `De ${FICHAS} exercícios, escolhemos os seus: entre ${EN_TU_PLAN.min} e ${EN_TU_PLAN.max}, ` +
      "pensados para o seu trabalho, as suas horas de voz e o seu dia.",
    puntos: [
      {
        titulo: "Conte como você usa a voz",
        texto: `Seu trabalho, suas horas de voz e ${PREGUNTAS} perguntas de segurança, curtas e de sim ou não.`,
      },
      {
        titulo: "Deixe a gente te ouvir",
        texto: "Você lê uma frase em voz alta e faz uma sirene suave. São trinta segundos, e o áudio não sai do seu aparelho.",
      },
      { titulo: "Receba seu plano", texto: "Seus exercícios, quanto de cada um e quando parar." },
      {
        titulo: "Pratique alguns minutos por dia",
        texto: "Se um dia a voz estiver cansada ou você não puder fazer barulho, a sessão desse dia se adapta.",
      },
    ],
  },
  {
    id: "momento",
    eyebrow: "A qualquer hora",
    titulo: "Cabe no seu dia.",
    cuerpo:
      `Alguns minutos por dia, no celular e sem instalar nada. E dá para fazer ${SILENCIOSAS} ` +
      "exercícios sem ninguém ouvir.",
    puntos: [
      { titulo: "No caminho", texto: "No ônibus ou no metrô, com o fone.", foto: { src: FOTOS.autobus, alt: "A mesma mulher sorri com o celular na janela do ônibus." } },
      { titulo: "Antes da aula", texto: "Cinco minutos na sala, antes de todo mundo chegar.", foto: { src: FOTOS.aula, alt: "Ela sorri olhando o celular, em pé numa sala de aula vazia." } },
      { titulo: "No intervalo", texto: "Entre duas ligações, sem sair da cadeira.", foto: { src: FOTOS.oficina, alt: "Ela ri olhando o celular na mesa do escritório, com o headset no pescoço." } },
      { titulo: "Antes da reunião", texto: "Na recepção, enquanto espera a sua vez.", foto: { src: FOTOS.reunion, alt: "Ela sorri com o celular, sentada na recepção de um escritório, vestida para uma reunião." } },
      { titulo: "No fim do dia", texto: "No sofá, para a voz desacelerar com calma.", foto: { src: FOTOS.sofa, alt: "Ela sorri com o celular no sofá, coberta com uma manta, à noite." } },
    ],
  },
  {
    id: "asistente",
    eyebrow: "Sempre à mão",
    titulo: "Você nunca está por conta própria com a sua voz.",
    cuerpo:
      "Um assistente especialista em voz que conhece o seu plano. Pergunte a qualquer hora como se " +
      "faz um exercício, por que ele está no seu plano ou o que fazer se hoje a voz está cansada.",
    puntos: [
      { titulo: "A qualquer hora", texto: "Antes da aula, no meio do plantão ou num domingo à noite." },
      { titulo: "Conhecendo o seu plano", texto: "Responde sobre os seus exercícios, não com dicas genéricas." },
      { titulo: "Com informação confiável", texto: "Usa a mesma biblioteca de exercícios do seu plano." },
    ],
  },
  {
    id: "diferenciales",
    eyebrow: "Por que o Voice Comfort",
    titulo: "A melhor e mais completa plataforma para cuidar da sua voz.",
    puntos: [
      { titulo: "Exercícios com fundamento", texto: "Baseados na prática profissional da voz e em estudos publicados." },
      { titulo: "Você ouve como deve soar", texto: "Os exercícios com som vêm com áudio de exemplo no seu idioma." },
      { titulo: "Você vê a sua voz ao vivo", texto: "Enquanto pratica, vê o seu tom e o seu volume na tela." },
      {
        titulo: "Leituras que te escutam",
        texto: "Quando você lê em voz alta, marcamos no texto as palavras que saíram claras. A sua voz não sai do seu aparelho.",
      },
      { titulo: "Também para os dias ruins", texto: `${SIN_CARGA} exercícios que não exigem esforço da voz, para quando ela está cansada.` },
      { titulo: "Seu progresso à vista", texto: "Veja quantas sessões já fez e como vai o seu plano." },
    ],
  },
  {
    id: "seguridad",
    eyebrow: "Sua segurança em primeiro lugar",
    titulo: "Cuidamos da sua voz com critério.",
    cuerpo:
      `Antes de criar o seu plano, fazemos ${PREGUNTAS} perguntas de segurança. Se alguma resposta ` +
      "indicar que vale a pena passar por um profissional, dizemos com clareza e indicamos quem " +
      "procurar.",
    puntos: [
      { titulo: "Perguntas antes de começar", texto: "Curtas e de sim ou não, para o plano partir de como está a sua voz hoje." },
      { titulo: "Avisamos quando procurar alguém", texto: "Se algo precisa do olhar de um profissional, dizemos e indicamos com que urgência." },
      { titulo: "Um plano que pega leve quando precisa", texto: "Quando a voz está cansada, o plano fica mais leve para você seguir praticando com tranquilidade." },
    ],
  },
  {
    id: "precio",
    eyebrow: "Preço",
    titulo: "Um ano inteiro cuidando da sua voz.",
    cuerpo:
      `Todo o Voice Comfort por ${PRECIO.moneda} ${PRECIO.lanzamientoUsd} por ano.`,
    puntos: [
      { titulo: "Seu plano personalizado", texto: "Feito para o seu trabalho e o seu dia, e muda com você." },
      { titulo: `Os ${FICHAS} exercícios`, texto: "Explicados passo a passo, com áudio de exemplo no seu idioma." },
      { titulo: "O assistente especialista em voz", texto: "Sempre à mão, com o seu plano na frente." },
      { titulo: "Seu progresso, sessão a sessão", texto: "Veja o que já fez e o que vem a seguir." },
      { titulo: "Cancele com um clique", texto: "Na sua conta, sem precisar falar com ninguém." },
      /* A garantia dos catorze dias. Contada inteira em `copy.ts`. */
      {
        titulo: "Garantia de catorze dias",
        texto: "Se não for para você, chame a gente no WhatsApp dentro dos primeiros catorze dias e devolvemos o valor pago.",
        destacado: true,
      },
    ],
  },
  {
    id: "preguntas",
    eyebrow: "Perguntas frequentes",
    titulo: "O que perguntam antes de começar.",
    puntos: [
      { titulo: "Preciso saber música ou cantar?", texto: "Não. O Voice Comfort trabalha a voz falada: a que você usa para dar aula, vender ou conduzir uma reunião." },
      { titulo: "Quanto tempo por dia?", texto: "Você escolhe: três, sete, quinze ou vinte e cinco minutos. O cronômetro avisa quando parar." },
      { titulo: "E se um dia eu não puder fazer barulho?", texto: "É só marcar ao começar, e a sessão desse dia usa exercícios silenciosos." },
      { titulo: "E se a minha voz estiver cansada?", texto: "É só marcar, e a sessão fica mais leve. Se algo precisar do olhar de um profissional, a gente avisa." },
      { titulo: "Preciso instalar alguma coisa?", texto: "Não. Abre no navegador do celular ou do computador." },
      { titulo: "Em que idiomas está disponível?", texto: "Em português, espanhol e inglês, com os áudios de exemplo em cada idioma." },
      { titulo: "Como funciona a garantia?", texto: "Se nos primeiros catorze dias você decidir que não é para você, chame a gente no WhatsApp e devolvemos o valor pago." },
      // 16/09/2026: dizia «… E avisamos antes da renovação.»; esse aviso não existe (o dono: tirar).
      { titulo: "Posso cancelar quando quiser?", texto: "Sim, com um clique na sua conta." },
    ],
  },
  {
    id: "cierre",
    eyebrow: "Comece hoje",
    titulo: "Sua voz te acompanha o dia todo. Dê a ela alguns minutos.",
    cuerpo: "Crie seu plano em cinco minutos e comece a cuidar dela hoje mesmo.",
  },
];

export const PIE_PT =
  // 16/09: «produto de saúde» → «dispositivo médico», como el castellano aprobado y el inglés.
  "O Voice Comfort treina uma habilidade. Não é um dispositivo médico, não diagnostica e não " +
  "substitui a consulta com um profissional. Quando algo precisa ser avaliado, avisamos e " +
  "indicamos a pessoa certa.";
